import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Check, RotateCw, X, Eraser, ShieldCheck } from 'lucide-react';
import { ouvrirFenetre, fermerFenetre } from '../utils/pauseGlobale';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Decoupe l'image au plus pres du trace pour que la signature remplisse le cadre du PDF
const recadrer = (source) => {
  const ctx = source.getContext('2d');
  const donnees = ctx.getImageData(0, 0, source.width, source.height).data;
  let xMin = source.width, yMin = source.height, xMax = 0, yMax = 0, trouve = false;

  for (let y = 0; y < source.height; y++) {
    for (let x = 0; x < source.width; x++) {
      if (donnees[(y * source.width + x) * 4 + 3] > 12) {
        trouve = true;
        if (x < xMin) xMin = x;
        if (x > xMax) xMax = x;
        if (y < yMin) yMin = y;
        if (y > yMax) yMax = y;
      }
    }
  }
  if (!trouve) return null;

  const marge = 12;
  xMin = Math.max(0, xMin - marge);
  yMin = Math.max(0, yMin - marge);
  xMax = Math.min(source.width, xMax + marge);
  yMax = Math.min(source.height, yMax + marge);

  const l = xMax - xMin;
  const h = yMax - yMin;
  const sortie = document.createElement('canvas');
  sortie.width = l;
  sortie.height = h;
  sortie.getContext('2d').drawImage(source, xMin, yMin, l, h, 0, 0, l, h);
  return sortie.toDataURL('image/png');
};

const SignatureDocument = ({ chantierId, typeDocument, onSigne, orientation }) => {
  const [apercu, setApercu] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [accepte, setAccepte] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [horizontal, setHorizontal] = useState(true);
  const [forcer, setForcer] = useState(true);
  const [aSigne, setASigne] = useState(false);
  const [etape, setEtape] = useState('lecture');
  const [apercuUrl, setApercuUrl] = useState(null);
  const [maintenant, setMaintenant] = useState('');
  const [signeUrl, setSigneUrl] = useState(null);
  const [voirSigne, setVoirSigne] = useState(false);

  const toile = useRef(null);
  const dessine = useRef(false);

  const charger = async () => {
    try {
      const r = await axios.get(`${API}/api/documents/${chantierId}/apercu/${typeDocument}`);
      setApercu(r.data.data);
    } catch (e) {
      setApercu(null);
    }
  };

  useEffect(() => { charger(); }, [chantierId, typeDocument]);

  // Horodatage rafraichi chaque minute pendant la signature
  useEffect(() => {
    const maj = () => {
      const n = new Date();
      const p = (x) => String(x).padStart(2, '0');
      setMaintenant(`${p(n.getDate())}/${p(n.getMonth() + 1)}/${n.getFullYear()} à ${p(n.getHours())}h${p(n.getMinutes())}`);
    };
    maj();
    const t = setInterval(maj, 30000);
    return () => clearInterval(t);
  }, []);

  // Precharge le document signe des qu'il existe
  useEffect(() => {
    if (!apercu || !apercu.deja_signe || signeUrl || !apercu.document?.id) return;
    let vivant = true;
    const jeton = localStorage.getItem('token');
    axios.get(`${API}/api/documents/${chantierId}/fichier/${apercu.document.id}?image=true`, {
      responseType: 'blob',
      headers: jeton ? { Authorization: `Bearer ${jeton}` } : {}
    })
      .then((r) => { if (vivant) setSigneUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [apercu, chantierId]);

  // Precharge le document en arriere-plan pour un affichage instantane
  useEffect(() => {
    if (!apercu || apercu.deja_signe || apercuUrl) return;
    let vivant = true;
    const jeton = localStorage.getItem('token');
    axios.get(`${API}/api/documents/${chantierId}/previsualiser/${typeDocument}`, {
      responseType: 'blob',
      headers: jeton ? { Authorization: `Bearer ${jeton}` } : {}
    })
      .then((r) => { if (vivant) setApercuUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [apercu, chantierId, typeDocument]);

  const ouvrir = () => {
    // La fenetre s'ouvre tout de suite, le reste suit en arriere-plan
    setEtape('lecture');
    ouvrirFenetre();
    setOuvert(true);
    charger();
    if (apercuUrl) return;
    (async () => {
    try {
      const jeton = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/documents/${chantierId}/previsualiser/${typeDocument}`, {
        responseType: 'blob',
        headers: jeton ? { Authorization: `Bearer ${jeton}` } : {}
      });
      setApercuUrl(URL.createObjectURL(r.data));
      } catch (e) {
        setApercuUrl(null);
      }
    })();
  };

  useEffect(() => {
    const verifier = () => setHorizontal(window.innerWidth > window.innerHeight);
    verifier();
    window.addEventListener('resize', verifier);
    window.addEventListener('orientationchange', verifier);
    return () => {
      window.removeEventListener('resize', verifier);
      window.removeEventListener('orientationchange', verifier);
    };
  }, []);

  // Préparer la zone de dessin, et la réajuster à chaque changement de taille
  useEffect(() => {
    if (!ouvert || etape !== 'signature') return;

    const preparer = () => {
      const c = toile.current;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const ratio = window.devicePixelRatio || 1;
      c.width = Math.round(rect.width * ratio);
      c.height = Math.round(rect.height * ratio);
      const ctx = c.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111827';
      setASigne(false);
    };

    const t = setTimeout(preparer, 60);
    const observateur = new ResizeObserver(preparer);
    if (toile.current) observateur.observe(toile.current);

    return () => { clearTimeout(t); observateur.disconnect(); };
  }, [ouvert, etape]);

  const position = (e) => {
    const c = toile.current;
    const rect = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const commencer = (e) => {
    if (!accepte) return;
    e.preventDefault();
    dessine.current = true;
    const ctx = toile.current.getContext('2d');
    const { x, y } = position(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const tracer = (e) => {
    if (!accepte || !dessine.current) return;
    e.preventDefault();
    const ctx = toile.current.getContext('2d');
    const { x, y } = position(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setASigne(true);
  };

  const arreter = () => { dessine.current = false; };

  const effacer = () => {
    const c = toile.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setASigne(false);
  };

  const valider = async () => {
    setErreur('');
    if (!accepte) return setErreur('Le client doit accepter les termes');
    if (!aSigne) return setErreur('La signature est vide');

    setEnCours(true);
    try {
      const image = recadrer(toile.current);
      if (!image) { setEnCours(false); return setErreur('La signature est vide'); }
      const r = await axios.post(`${API}/api/documents/${chantierId}/signer`, {
        type_document: typeDocument,
        signature: image,
        accepte_termes: true,
      });
      fermerFenetre();
      setOuvert(false);
      setAccepte(false);
      setASigne(false);
      setEtape('lecture');

      await charger();
      if (onSigne) onSigne(r.data.data);
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Échec de la signature');
    } finally {
      setEnCours(false);
    }
  };

  if (!apercu) return null;

  const d = apercu.donnees || {};
  const carte = { background: '#fff', border: '1px solid #F3F4F6', borderRadius: '12px', padding: '17px' };

  if (apercu.deja_signe) {
    return (
      <>
      <div style={carte}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
          <ShieldCheck size={18} color="#10B981" />
          <span style={{ fontSize: '15px', fontWeight: 500 }}>{apercu.titre}</span>
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
          Signé par {apercu.document?.signe_par}
        </div>
        <div style={{ fontSize: '11.5px', color: '#9CA3AF' }}>
          {apercu.document?.horodatage
            ? new Date(apercu.document.horodatage).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
            : ''}
        </div>
        <button onClick={async () => {
            setVoirSigne(true);
            if (signeUrl) return;
            try {
              const jeton = localStorage.getItem('token');
              const r = await axios.get(`${API}/api/documents/${chantierId}/fichier/${apercu.document?.id}?image=true`, {
                responseType: 'blob',
                headers: jeton ? { Authorization: `Bearer ${jeton}` } : {}
              });
              setSigneUrl(URL.createObjectURL(r.data));
            } catch (e) {}
          }}
          style={{ display: 'block', width: '100%', marginTop: '13px', padding: '11px', border: '1px solid #E5E7EB', borderRadius: '9px', textAlign: 'center', fontSize: '14px', color: '#374151', background: '#fff', cursor: 'pointer' }}>
          Voir le document
        </button>
      </div>

      {voirSigne && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 200, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>{apercu.titre}</span>
            <X size={22} color="#6B7280" style={{ cursor: 'pointer' }} onClick={() => setVoirSigne(false)} />
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#F3F4F6' }}>
            {signeUrl ? (
              <img src={signeUrl} alt="Document signé" style={{ width: '100%', display: 'block' }} />
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>Chargement…</div>
            )}
          </div>
        </div>
      )}
    </>
    );
  }

  return (
    <>
      <div style={carte}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
          <FileText size={18} color="#10B981" />
          <span style={{ fontSize: '15px', fontWeight: 500 }}>{apercu.titre}</span>
        </div>
        <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '14px' }}>
          À faire signer par le client
        </div>
        <button onClick={ouvrir}
          style={{ width: '100%', padding: '13px', border: 'none', borderRadius: '9px', background: '#10B981', color: '#fff', fontSize: '15px', cursor: 'pointer' }}>
          Lire et signer le document
        </button>
      </div>

      {ouvert && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 200, display: 'flex', flexDirection: 'column' }}>

          {!horizontal && !forcer ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', textAlign: 'center' }}>
              <RotateCw size={54} color="#10B981" style={{ marginBottom: '22px' }} />
              <div style={{ fontSize: '19px', fontWeight: 600, marginBottom: '10px' }}>Tournez le téléphone</div>
              <div style={{ fontSize: '15px', color: '#6B7280', lineHeight: 1.5, marginBottom: '30px' }}>
                Passez en mode horizontal pour que le client puisse signer confortablement.
              </div>
              <button onClick={() => setForcer(true)}
                style={{ padding: '12px 22px', border: '1px solid #E5E7EB', borderRadius: '9px', background: '#fff', color: '#6B7280', fontSize: '14px', cursor: 'pointer', marginBottom: '14px' }}>
                Signer quand même
              </button>
              <button onClick={() => setOuvert(false)}
                style={{ border: 'none', background: 'transparent', color: '#9CA3AF', fontSize: '14px', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ fontSize: '14px', fontWeight: 500 }}>{apercu.titre}</span>
                <X size={22} color="#6B7280" style={{ cursor: 'pointer' }} onClick={() => { fermerFenetre(); setOuvert(false); }} />
              </div>

              {etape === 'lecture' ? (
                <>
                  <div style={{ padding: '12px 16px', background: '#FFFBEB', fontSize: '12.5px', color: '#92400E', lineHeight: 1.5 }}>
                    Présentez le document au client et laissez-le en prendre connaissance avant de poursuivre.
                  </div>
                  <div style={{ flex: 1, minHeight: 0, background: '#F3F4F6' }}>
                    {apercuUrl ? (
                      <div style={{ width: '100%', height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#F3F4F6' }}>
                        <img src={apercuUrl} alt="Document" style={{ width: '100%', display: 'block' }} />
                      </div>
                    ) : (
                      <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                        Chargement du document…
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '14px 16px 22px' }}>
                    <button onClick={() => setEtape('signature')}
                      style={{ width: '100%', padding: '15px', border: 'none', borderRadius: '10px', background: '#10B981', color: '#fff', fontSize: '16px', cursor: 'pointer' }}>
                      Le client a lu le document
                    </button>
                  </div>
                </>
              ) : (
              <>
              <div style={{ padding: '12px 16px', background: '#F9FAFB', fontSize: '12.5px', color: '#374151', lineHeight: 1.6 }}>
                <strong>{d.nom_client}</strong>
                {d.adresse ? ` · ${d.adresse}` : ''}
                {d.ville ? ` · ${d.code_postal} ${d.ville}` : ''}
                {d.date ? ` · ${d.date}` : ''}
              </div>

              <div style={{ padding: '12px 16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={accepte} onChange={(e) => setAccepte(e.target.checked)}
                    style={{ width: '20px', height: '20px', marginTop: '1px', flexShrink: 0, accentColor: '#10B981' }} />
                  <span style={{ fontSize: '12.5px', color: '#374151', lineHeight: 1.5 }}>
                    {typeDocument === 'bordereau' ? (
                      <>
                        En cochant cette case, je soussigné(e) <strong>{d.nom_client || '—'}</strong>, agissant en
                        qualité de maître d'ouvrage, déclare avoir lu l'intégralité du présent document et signer en
                        toute connaissance de cause. Je reconnais que l'installation sera réalisée à mon domicile,
                        <strong> {d.adresse_complete || '—'}</strong>, sur la partie de toiture photographiée lors de
                        la prévisite{orientation ? <>, orientée <strong>{orientation.libelle}</strong></> : ''},
                        conformément à ce qui a été convenu avec les techniciens. Fait le <strong>{maintenant}</strong>.
                        Ma signature électronique a la même valeur juridique qu'une signature manuscrite.
                      </>
                    ) : (
                      <>
                        En cochant cette case, je soussigné(e) <strong>{d.nom_client || '—'}</strong>, agissant en
                        qualité de maître d'ouvrage, certifie sur l'honneur avoir lu l'intégralité du présent document
                        et signer en toute connaissance de cause. Je déclare avoir procédé à la visite des travaux
                        réalisés à mon domicile, <strong>{d.adresse_complete || '—'}</strong>, constater qu'ils ont été
                        exécutés conformément à ce qui avait été convenu, et les accepter <strong>sans aucune réserve</strong>.
                        Fait le <strong>{maintenant}</strong>. Ma signature électronique a la même valeur juridique
                        qu'une signature manuscrite.
                      </>
                    )}
                  </span>
                </label>
              </div>

              <div style={{ flex: 1, padding: '0 16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ fontSize: '11.5px', color: accepte ? '#9CA3AF' : '#B45309', marginBottom: '5px' }}>{accepte ? 'Signature du client' : 'Cochez la case pour pouvoir signer'}</div>
                <canvas ref={toile}
                  onMouseDown={commencer} onMouseMove={tracer} onMouseUp={arreter} onMouseLeave={arreter}
                  onTouchStart={commencer} onTouchMove={tracer} onTouchEnd={arreter}
                  style={{
                    flex: 1, width: '100%',
                    border: `2px dashed ${accepte ? '#D1D5DB' : '#E5E7EB'}`,
                    borderRadius: '10px', touchAction: 'none',
                    background: accepte ? '#fff' : '#F9FAFB',
                    minHeight: '120px',
                    opacity: accepte ? 1 : 0.6,
                    transition: 'all .2s'
                  }} />
              </div>

              {erreur && (
                <div style={{ padding: '9px 16px', color: '#991B1B', fontSize: '13px' }}>{erreur}</div>
              )}

              <div style={{ display: 'flex', gap: '10px', padding: '12px 16px 18px' }}>
                <button onClick={effacer}
                  style={{ padding: '13px 18px', border: '1px solid #E5E7EB', borderRadius: '9px', background: '#fff', color: '#6B7280', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <Eraser size={16} /> Effacer
                </button>
                <button onClick={valider} disabled={enCours || !accepte || !aSigne}
                  style={{
                    flex: 1, padding: '13px', border: 'none', borderRadius: '9px',
                    background: (!accepte || !aSigne) ? '#F3F4F6' : '#10B981',
                    color: (!accepte || !aSigne) ? '#9CA3AF' : '#fff',
                    fontSize: '15px', cursor: (!accepte || !aSigne) ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}>
                  <Check size={17} /> {enCours ? 'Enregistrement…' : 'Valider la signature'}
                </button>
              </div>
              </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SignatureDocument;
